// Request/response contracts for the wave-1 API surface (PRD §16).
// These zod schemas are the single source for validation, JSON Schema and OpenAPI.
import { z } from "zod";
import {
  AUTONOMY_PRESETS,
  DELIVERY_TARGETS,
  ENVIRONMENT_KINDS,
  EXTRACTION_STATES,
  PRECISIONS,
  PROJECT_STATES,
  REQUIREMENT_KINDS,
  REQUIREMENT_STATES,
  RISK_LEVELS,
  ROLES,
  TASK_COLUMNS,
  TASK_ORIGINS,
  TASK_PLACEMENT_OUTCOMES,
  TASK_PLACEMENT_REASONS,
  TASK_PLACEMENTS,
  TASK_SIDE_STATES,
  TASK_STATUSES,
  TASK_TYPES, RESOURCE_CLASSES } from "./enums.ts";
import { COMPLETION_KINDS, codeVerdictSchema } from "./onboarding.ts";
import { blockReasonSchema } from "./block-reasons.ts";

const id = z.uuid();
const timestamp = z.iso.datetime();
const taskStatus = z.enum(TASK_STATUSES as unknown as [string, ...string[]]);

// ---------- Projects (RF-PROJ-01..07) ----------

export const workspaceInputSchema = z.object({
  runner_id: z.string().min(1).max(200),
  operating_system: z.enum(["linux", "macos", "windows"]),
  root_path: z.string().min(1).max(1024).describe("Absolute path, meaningful only on the given runner."),
  subpath: z.string().max(1024).default(""),
  commands: z
    .object({
      /**
       * D37.2: dependency install run before build/test in the sandbox (registry network: package registries only).
       * Set by a person only (signed-in session); agents and the assistant may only propose it (confirm click).
       */
      prepare: z.string().max(2000).optional(),
      build: z.string().max(2000).optional(),
      test: z.string().max(2000).optional(),
      teardown: z.string().max(2000).optional(),
      /** D37.2: run the packages' install lifecycle scripts during prepare (default off; a person's opt-in). */
      allow_install_scripts: z.boolean().optional(),
      /** Human opt-in for diagnostic/repair shell commands inside the verified sandbox. */
      agent_shell: z.boolean().optional(),
    })
    .default({}),
});

export const environmentInputSchema = z.object({
  kind: z.enum(ENVIRONMENT_KINDS),
  name: z.string().min(1).max(120),
  allowed_urls: z.array(z.url()).max(50).default([]),
  network_policy: z.enum(["deny_all", "allowlist", "open"]).default("allowlist"),
  secret_refs: z.array(z.string().max(200)).max(50).default([]),
});

export const createProjectSchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().max(2000).default(""),
  timezone: z.string().max(64).default("UTC"),
  repository_url: z.string().max(500).nullable().default(null),
  base_branch: z.string().max(200).default("main"),
  delivery_target: z.enum(DELIVERY_TARGETS).default("pull_request"),
  autonomy_preset: z.enum(AUTONOMY_PRESETS).default("observation"),
  auto_execute: z.boolean().default(false),
  max_concurrent_tasks: z.number().int().min(1).max(50).default(3),
  workspaces: z.array(workspaceInputSchema).max(10).default([]),
  environments: z.array(environmentInputSchema).max(10).default([]),
});
export type CreateProjectInput = z.infer<typeof createProjectSchema>;

export const readinessCheckSchema = z.object({
  check: z.enum([
    "repository",
    "base_branch",
    "workspace_authorized",
    "runner_healthy",
    "executor_available",
    "test_command",
    "eligible_route",
  ]),
  status: z.enum(["passed", "failed", "unknown", "not_applicable"]),
  cause: z.string().nullable(),
  checked_at: timestamp.nullable(),
});
export type ReadinessCheck = z.infer<typeof readinessCheckSchema>;

export const projectSchema = z.object({
  id,
  organization_id: id,
  name: z.string(),
  description: z.string(),
  timezone: z.string(),
  repository_url: z.string().nullable(),
  base_branch: z.string(),
  delivery_target: z.enum(DELIVERY_TARGETS),
  autonomy_preset: z.enum(AUTONOMY_PRESETS),
  auto_execute: z.boolean(),
  max_concurrent_tasks: z.number().int(),
  /** D37.3: the project's content language override (null or absent = the organization's). */
  content_language: z.string().nullable().optional(),
  state: z.enum(PROJECT_STATES),
  version: z.number().int(),
  created_at: timestamp,
  updated_at: timestamp,
});
export type Project = z.infer<typeof projectSchema>;

/** Aggregate shown in the portfolio (RF-UI-07/08): derived from task facts only. */
export const projectSummarySchema = projectSchema.extend({
  aggregate_state: z.enum(["not_started", "running", "needs_attention", "blocked", "completed", "paused"]),
  progress: z.object({
    accepted_required: z.number().int(),
    total_required: z.number().int(),
    scope_label: z.string().describe("For example 'PRD v3' — the denominator's scope."),
  }),
  active_agents: z.number().int(),
  attention_reason: z.string().nullable(),
  /**
   * D37: the task behind the attention reason, with its structured block reason and how many open tasks wait for it.
   * Optional: additive field.
   */
  attention: z
    .object({
      task_id: id,
      task_number: z.number().int(),
      title: z.string(),
      status: taskStatus,
      block_reason: blockReasonSchema.nullable(),
      dependents: z.number().int().nonnegative(),
    })
    .nullable()
    .optional(),
  last_activity_at: timestamp.nullable(),
});
export type ProjectSummary = z.infer<typeof projectSummarySchema>;

// ---------- Tasks (RF-TASK-01..04, RF-FLOW-01..06) ----------

export const createTaskSchema = z.object({
  title: z.string().trim().min(1).max(300),
  description: z.string().max(20_000).default(""),
  type: z.enum(TASK_TYPES).default("feature"),
  risk: z.enum(RISK_LEVELS).default("medium"),
  origin: z.enum(TASK_ORIGINS).default("manual"),
  desired_priority: z.number().int().min(0).max(100).nullable().default(null),
  due_at: timestamp.nullable().default(null),
  parent_task_id: id.nullable().default(null),
  depends_on: z.array(id).max(100).default([]),
  acceptance_criteria: z.array(z.string().min(1).max(2000)).max(100).default([]),
  evidence: z.string().max(20_000).default(""),
  requirement_ids: z.array(id).max(200).default([]),
  /** F08 (AC-66): capacity class for admission; a non-default class needs the maintainer role. */
  resource_class: z.enum(RESOURCE_CLASSES).default("standard"),
  placement: z
    .enum(TASK_PLACEMENTS)
    .default("backlog")
    .describe(
      "Where the new task goes: `backlog` (default, unchanged behaviour), `prioritized` (top of the project queue and promoted to To Do when its dependencies allow) or `start_now` (prioritized, then started through the same readiness, capacity and engine checks as project execution). `prioritized`/`start_now` require the maintainer role.",
    ),
});
export type CreateTaskInput = z.infer<typeof createTaskSchema>;

export const taskSchema = z.object({
  id,
  organization_id: id,
  project_id: id,
  number: z.number().int(),
  title: z.string(),
  description: z.string(),
  original_text: z.string(),
  type: z.enum(TASK_TYPES),
  status: taskStatus,
  return_column: z.enum(TASK_COLUMNS).nullable(),
  status_reason: z.string().nullable(),
  /** D37: structured reason of a side state (code + typed params); the English status_reason stays for logs. Optional: additive. */
  block_reason: blockReasonSchema.nullable().optional(),
  risk: z.enum(RISK_LEVELS),
  origin: z.enum(TASK_ORIGINS),
  resource_class: z.enum(RESOURCE_CLASSES),
  priority_score: z.number().nullable(),
  priority_reason: z.string().nullable(),
  manual_priority: z.number().int().nullable(),
  due_at: timestamp.nullable(),
  parent_task_id: id.nullable(),
  prd_version_id: id.nullable(),
  delivery_target: z.enum(DELIVERY_TARGETS),
  acceptance_criteria: z.array(z.object({ id: z.string(), text: z.string(), satisfied: z.boolean() })),
  depends_on: z.array(id),
  requirement_refs: z.array(z.string()),
  version: z.number().int(),
  created_at: timestamp,
  updated_at: timestamp,
  completed_at: timestamp.nullable(),
  /** How the task reached `done` (migration 0017); null until it does, and optional for rows written before it. */
  completion_kind: z.enum(COMPLETION_KINDS).nullable().optional(),
});
export type Task = z.infer<typeof taskSchema>;

/** Outcome of the placement requested on task creation (never an error that loses the task). */
export const taskPlacementResultSchema = z.object({
  requested: z.enum(TASK_PLACEMENTS),
  applied: z.enum(TASK_PLACEMENT_OUTCOMES),
  /** Machine-readable cause when `applied` is weaker than `requested` (or a caveat); null otherwise. */
  reason_code: z.enum(TASK_PLACEMENT_REASONS).nullable(),
  reason: z.string().nullable(),
  /** Manual priority set by the placement (null for `backlog` without desired_priority). */
  manual_priority: z.number().int().nullable(),
  /** Deterministic engine execution id when the task run was started. */
  execution_id: z.string().optional(),
});
export type TaskPlacementResult = z.infer<typeof taskPlacementResultSchema>;

export const createTaskResponseSchema = taskSchema.extend({ placement_result: taskPlacementResultSchema });
export type CreateTaskResponse = z.infer<typeof createTaskResponseSchema>;

export const TASK_ACTIONS = [
  "start_analysis",
  "mark_ready",
  "start",
  "submit_for_testing",
  "gates_passed",
  "gates_failed",
  "review_approved",
  "review_rejected",
  "integrated",
  "release_confirmed",
  "complete",
  "block",
  "unblock",
  "wait_resource",
  "resource_available",
  "pause",
  "resume",
  "fail",
  "retry",
  "cancel",
  "reopen",
  "admin_override",
  // D23: the council escalates a decision to a human; the human approves (back to production) or rejects (blocked).
  "await_human",
  "human_approved",
  "human_rejected",
  // Existing-project onboarding: the code analysis (or a human) closes a task whose work is already in the repository.
  "mark_existing",
] as const;
export type TaskAction = (typeof TASK_ACTIONS)[number];

export const taskActionRequestSchema = z.object({
  action: z.enum(TASK_ACTIONS),
  expected_version: z.number().int().nonnegative(),
  reason: z.string().max(2000).optional(),
  /** Required for admin_override: target status and justification (RF-FLOW-03). */
  override_target: taskStatus.optional(),
});
export type TaskActionRequest = z.infer<typeof taskActionRequestSchema>;

export const boardQuerySchema = z.object({
  prd_version_id: id.optional(),
  risk: z.enum(RISK_LEVELS).optional(),
  origin: z.enum(TASK_ORIGINS).optional(),
  type: z.enum(TASK_TYPES).optional(),
  search: z.string().max(200).optional(),
});

/** Attempt currently working on a board card (only for tasks in a working column). */
export const taskCardAttemptSchema = z.object({
  id,
  number: z.number().int(),
  state: z.enum(["running", "waiting_resource"]),
  started_at: timestamp,
  /** Agent role of the attempt (builder, tester, critic...). */
  role: z.string(),
  agent_profile_name: z.string().nullable(),
  /** Model requested by the latest model call, else the pinned route (may be a router alias such as "auto/coding:free"). */
  requested_model: z.string().nullable(),
  /** Real model reported by the provider/router for the latest call that returned one; null while unknown. */
  effective_model: z.string().nullable(),
});
export type TaskCardAttempt = z.infer<typeof taskCardAttemptSchema>;

/** Board card: task without long text fields (full detail via GET /v1/tasks/{id}). */
export const taskCardSchema = taskSchema
  .omit({ description: true, original_text: true, acceptance_criteria: true })
  .extend({
    acceptance_summary: z.object({ total: z.number().int(), satisfied: z.number().int() }),
    /** When the task entered its current status. Optional: added to the board contract additively. */
    status_since: timestamp.optional(),
    /** Running/waiting attempt for tasks in a working column; null otherwise. Optional: additive field. */
    current_attempt: taskCardAttemptSchema.nullable().optional(),
  });
export type TaskCard = z.infer<typeof taskCardSchema>;

export const boardSchema = z.object({
  project_id: id,
  columns: z.array(z.object({ column: z.enum(TASK_COLUMNS), tasks: z.array(taskCardSchema) })),
  side_lane: z.array(z.object({ state: z.enum(TASK_SIDE_STATES), tasks: z.array(taskCardSchema) })),
  board_version: z.string().describe("Cursor of the last event reflected in this board."),
  generated_at: timestamp,
});
export type Board = z.infer<typeof boardSchema>;

// ---------- PRDs (RF-PRD-01..06) ----------

export const PRD_SOURCE_KINDS = ["text", "markdown", "txt", "pdf", "docx"] as const;

export const importPrdSchema = z.object({
  title: z.string().trim().min(1).max(300),
  source_kind: z.enum(PRD_SOURCE_KINDS),
  /** Plain text/markdown content, or base64 for pdf/docx. */
  content: z.string().min(1).max(15_000_000),
  content_encoding: z.enum(["utf8", "base64"]).default("utf8"),
  start_execution: z.boolean().default(false),
  intentional_new_version: z.boolean().default(false),
});
export type ImportPrdInput = z.infer<typeof importPrdSchema>;

export const requirementSchema = z.object({
  id,
  prd_version_id: id,
  external_id: z.string(),
  title: z.string(),
  body: z.string(),
  kind: z.enum(REQUIREMENT_KINDS),
  mandatory: z.boolean(),
  section_ref: z.string(),
  state: z.enum(REQUIREMENT_STATES),
  ambiguity: z.string().nullable(),
  task_ids: z.array(id),
});
export type Requirement = z.infer<typeof requirementSchema>;

export const prdImportResultSchema = z.object({
  import_id: id,
  prd_id: id,
  prd_version_id: id,
  version: z.number().int(),
  replayed: z.boolean().describe("True when the same Idempotency-Key returned a previous import."),
  duplicate_content_warning: z.boolean(),
  extraction_state: z.enum(EXTRACTION_STATES),
  extraction_notes: z.array(z.string()),
  requirement_count: z.number().int(),
  task_count: z.number().int(),
  pending_count: z.number().int(),
});
export type PrdImportResult = z.infer<typeof prdImportResultSchema>;

export const coverageRowSchema = z.object({
  requirement: requirementSchema,
  tasks: z.array(z.object({ id, number: z.number().int(), title: z.string(), status: taskStatus })),
  tests: z.array(z.object({ id: z.string(), result: z.string() })),
  evidence: z.array(z.object({ id: z.string(), kind: z.string() })),
  /** Latest codebase-analysis verdict of the requirement, when one exists. */
  code_verdict: codeVerdictSchema.nullable().optional(),
});

// ---------- Usage precision (RF-OBS-03) ----------

export const measuredValueSchema = z.object({
  value: z.number().nullable(),
  precision: z.enum(PRECISIONS),
});

// ---------- Members ----------

export const membershipSchema = z.object({ user_id: id, role: z.enum(ROLES) });
